// apps/client/src/pages/leads/edm/components/ContactHistoryDrawer.tsx
//
// Read-only slide-over: which campaigns this contact was in and what happened
// (edmContactAction contactHistory, server-filtered to campaigns the caller
// can access). Pattern: components/leads/LeadDetailDrawer.tsx.

import React, { useEffect, useState } from 'react';
import { History, X } from 'lucide-react';
import { SkeletonTable } from '../../../../components/common/SkeletonLoader';
import type { EdmContact, EdmContactHistoryRow } from '../../../../hooks/useEdmContacts';
import { edmContactErrorMessage } from '../../../../hooks/useEdmContacts';
import { EDM_UI, formatBrisbaneDateTime, statusChipClass, statusLabel } from '../../../../utils/edm';

interface Props {
  contact: EdmContact | null;
  loadHistory: (contactId: string) => Promise<{ history: EdmContactHistoryRow[] }>;
  onClose: () => void;
}

/** The delivery outcome in words, newest signal wins (complaint > bounce > delivered). */
export function outcomeLabel(outcome: EdmContactHistoryRow['outcome']): string {
  if (!outcome) return '–';
  if (typeof outcome === 'string') return outcome;
  if (outcome.complainedAt) return `Complained ${formatBrisbaneDateTime(outcome.complainedAt)}`.trim();
  if (outcome.bouncedAt) return `Bounced ${formatBrisbaneDateTime(outcome.bouncedAt)}`.trim();
  if (outcome.deliveredAt) return `Delivered ${formatBrisbaneDateTime(outcome.deliveredAt)}`.trim();
  return '–';
}

const ContactHistoryDrawer: React.FC<Props> = ({ contact, loadHistory, onClose }) => {
  const [rows, setRows] = useState<EdmContactHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contactId = contact?.id;

  useEffect(() => {
    if (!contactId) return;
    let active = true;
    setRows(null);
    setError(null);
    loadHistory(contactId)
      .then((res) => {
        if (active) setRows(res.history ?? []);
      })
      .catch((err) => {
        if (active) setError(edmContactErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [contactId, loadHistory]);

  if (!contact) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-label="Contact history"
        className="h-full w-full max-w-lg overflow-y-auto border-l border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-gray-800 bg-gray-900/95 px-5 py-4 backdrop-blur">
          <div>
            <h3 className={EDM_UI.heading}>
              <History className={EDM_UI.headingIcon} /> Campaign history
            </h3>
            <p className="mt-1 text-sm text-gray-400">{contact.email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" title="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          {error && <div className={EDM_UI.errorBanner}>{error}</div>}
          {!error && rows === null && <SkeletonTable rows={3} columns={3} />}
          {rows !== null && rows.length === 0 && (
            <p className="text-sm text-gray-400">This contact has not been in any campaign yet.</p>
          )}
          {rows !== null &&
            rows.map((r) => (
              <div key={r.campaignId} className="rounded-lg border border-gray-700 bg-gray-800/40 p-4 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-100">{r.name}</span>
                  <span className={`${EDM_UI.chip} ${statusChipClass(r.status)}`}>
                    {statusLabel(r.status)}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <dt className="text-gray-400">Sent</dt>
                  <dd className="text-gray-200">{formatBrisbaneDateTime(r.sentAt) || '–'}</dd>
                  <dt className="text-gray-400">Send status</dt>
                  <dd className="text-gray-200">{r.ledgerStatus || '–'}</dd>
                  <dt className="text-gray-400">Outcome</dt>
                  <dd className="text-gray-200">{outcomeLabel(r.outcome)}</dd>
                </dl>
              </div>
            ))}
        </div>
      </aside>
    </div>
  );
};

export default ContactHistoryDrawer;
